// ════════════════════════════════════════════════════════════════════════
// useIntersectionObserver.ts — Detecta cuando un elemento entra al viewport
// ════════════════════════════════════════════════════════════════════════
// CONCEPTO: useEffect con cleanup
// useEffect puede retornar una función de limpieza que React ejecuta:
//   1. Antes de que el componente se desmonte
//   2. Antes de volver a ejecutar el efecto (si cambian las dependencias)
//
// IntersectionObserver es una Web API nativa que notifica cuando un
// elemento entra o sale del área visible del usuario (viewport).
// Es más eficiente que el scroll event listener porque el navegador
// lo optimiza internamente.
// ════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import type { RefObject } from 'react';

// ── Opciones del hook ─────────────────────────────────────────────────
interface UseIntersectionObserverOptions {
  threshold?: number;    // 0 a 1: qué porcentaje del elemento debe ser visible
  rootMargin?: string;   // Margen extra antes de disparar (ej: "100px" para anticipar)
  once?: boolean;        // Si true, deja de observar después de la primera intersección
}

// ── Tipo de retorno ───────────────────────────────────────────────────
// Tupla [ref, isVisible] — el componente ata el ref al elemento del DOM
// y el hook le dice cuándo ese elemento es visible.
type UseIntersectionObserverReturn = [RefObject<HTMLDivElement>, boolean];

export function useIntersectionObserver(
  options: UseIntersectionObserverOptions = {},
): UseIntersectionObserverReturn {
  const { threshold = 0.1, rootMargin = '0px', once = true } = options;

  // useRef<HTMLDivElement> — referencia tipada al elemento DOM
  // null es el valor inicial porque el elemento no existe antes del primer render
  const ref = useRef<HTMLDivElement>(null);

  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    // Guardar la referencia al elemento en una variable local.
    // Esto es necesario porque ref.current puede cambiar entre
    // el momento en que el efecto corre y cuando el cleanup se ejecuta.
    const element = ref.current;

    // Si no hay elemento (aún no montado), no hacer nada
    if (!element) return;

    // Crear el observer con el callback que actualiza el estado
    const observer = new IntersectionObserver(
      ([entry]) => {
        // entry.isIntersecting es true cuando el elemento entra al viewport
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Si once=true, dejar de observar para liberar recursos
          if (once) {
            observer.unobserve(element);
          }
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(element); // Comenzar a observar el elemento

    // ── CLEANUP ────────────────────────────────────────────────────
    // Esta función se ejecuta cuando el componente se desmonta.
    // Sin esto, el observer seguiría vivo aunque el elemento no exista → memory leak.
    return () => {
      observer.unobserve(element);
      observer.disconnect();
    };
  }, [threshold, rootMargin, once]); // Solo re-ejecutar si cambian estas opciones

  return [ref, isVisible];
}
